import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import Spinner from '../components/ui/spinner';
import { ProjectAndCustomer } from '../project/projectType';
import ProjectApi from '../project/projectApi';
import { formatAddress } from '../lib/global_function';
import PhotoApi from '../photo/photoApi';
import { Photo } from '../photo/photoType';
import { Upload, Camera } from 'lucide-react';
import PhotoCarousel from '../photo/photoCarousel';

const PhotoPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Photo Gallery', path: '/main/project/photo' },
  ];

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ProjectAndCustomer | null>(null);
  const [projectsAndCustomers, setProjectsAndCustomers] = useState<ProjectAndCustomer[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [largePhotos, setLargePhotos] = useState<Photo[]>([]);

useEffect(() => {
  const fetchProjectsAndCustomers = async () => {
    setIsLoadingProjects(true);
    try {
      const fetchedProjectsAndCustomers = await ProjectApi.getProjectsAndCustomers();
      setProjectsAndCustomers(fetchedProjectsAndCustomers);

      const projectFromState = location.state?.selectedProject;
      if (projectFromState) {
        const projectId = projectFromState.project_id.toString();
        setSelectedProjectId(projectId);
        setSelectedCustomer(projectFromState);
        
        // Fetch photos immediately if project is available from state
        try {
          setLoading(true);
          const { smallPhotos, largePhotos } = await fetchAndSeparatePhotos('project', projectId);
          setPhotos(smallPhotos);
          setLargePhotos(largePhotos);
        } catch (error) {
          setErrorMessage(error instanceof Error ? error.message : 'Failed to load photos');
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    } finally {
      setIsLoadingProjects(false);
    }
  };
  fetchProjectsAndCustomers();
}, [location.state]);


  const handleProjectChange = async (projectId: string) => {
    setSelectedProjectId(projectId);
    const selected = projectsAndCustomers.find(
      (projCust) => projCust.project_id.toString() === projectId.toString()
    );
    setSelectedCustomer(selected || null);
    setPhotos([]);
    setLargePhotos([]);
    setErrorMessage(null);
    setMessage(null);

    if (projectId) {
      try {
        setLoading(true);
        const { smallPhotos, largePhotos } = await fetchAndSeparatePhotos('project', projectId);
        setPhotos(smallPhotos);
        setLargePhotos(largePhotos);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load photos');
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchAndSeparatePhotos = async (imageForTag: string, id: string) => {
    try {
      const allPhotos = await PhotoApi.getAllPhotoLinks(imageForTag, id);
      const smallPhotos = allPhotos.filter((photo: Photo) => photo.size_tag === 'small');
      const largePhotos = allPhotos.filter((photo: Photo) => photo.size_tag === 'original');
      return { smallPhotos, largePhotos };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to load photos');
    }
  };

  // Simple delete handler - just calls API and updates state
  // Throws on error, which PhotoCarousel catches and propagates via onError
  const handlePhotoDelete = async (photoId: number) => {
    await PhotoApi.delete(photoId.toString());
    setPhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== photoId));
    setLargePhotos(prevLargePhotos => prevLargePhotos.filter(photo => photo.id !== photoId));
  };

  // Handler for errors from PhotoCarousel
  const handlePhotoError = (message: string) => {
    setErrorMessage(message);
  };

  const handleUploadClick = () => {
    if (selectedCustomer) {
      navigate('/main/project/photo/upload', { state: { projectAndCustomer: selectedCustomer } });
    }
  };

  if (isLoadingProjects) {
    return (
      <>
        <HorizontalMenu />
        <Spinner />
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6 overflow-x-hidden">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-foreground mb-2 flex items-center gap-2">
              Project Photos
            </h1>
            <p className="text-sm text-gray-600">
              Select a project from the dropdown list to view and manage its photos.
            </p>
          </div>

          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {message && (
            <Alert variant="success" className="mb-6">
              {message}
            </Alert>
          )}

          <div className="space-y-3">
            {selectedCustomer && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <h2 className="text-lg font-semibold text-foreground mb-3">Customer Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
                  <p>
                    <span className="font-semibold">Name:</span>{' '}
                    {selectedCustomer.customer_first_name} {selectedCustomer.customer_last_name}
                  </p>
                  <p>
                    <span className="font-semibold">Email:</span> {selectedCustomer.customer_email}
                  </p>
                  <p>
                    <span className="font-semibold">Address:</span>{' '}
                    {formatAddress(
                      selectedCustomer.customer_address_1,
                      '',
                      selectedCustomer.customer_city,
                      selectedCustomer.customer_state,
                      selectedCustomer.customer_zip
                    )}
                  </p>
                  <p>
                    <span className="font-semibold">Phone:</span> {selectedCustomer.customer_phone}
                  </p>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-3">
                  Select Project
                  <span className="text-red-600 ml-1">*</span>
                </h2>
                <Select
                  id="project-select"
                  name="selectedProject"
                  value={selectedProjectId || ''}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  required
                >
                  <option value="">Select Project</option>
                  {projectsAndCustomers.map((projCust) => (
                    <option key={projCust.project_id} value={projCust.project_id}>
                      {projCust.name}
                      {projCust.project_description
                        ? ` - ${projCust.project_description.substring(0, 50)}`
                        : ''}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {loading && (
              <div className="flex justify-center py-12">
                <Spinner size="lg" fullScreen={false} />
              </div>
            )}

            {!loading && selectedProjectId && photos.length === 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex flex-col items-center gap-4">
                  <Camera className="size-16 text-gray-300" />
                  <div>
                    <p className="text-gray-600 mb-2">No photos found for this project.</p>
                    <p className="text-sm text-gray-500 mb-4">Upload photos to get started.</p>
                  </div>
                  <Button size="medium" onClick={handleUploadClick}>
                    <Upload size={18} />
                    Upload Photos
                  </Button>
                </div>
              </div>
            )}

            {!loading && selectedProjectId && photos.length > 0 && (
              <div className="text-center space-y-4">
                {/* Before Project Photos */}
                <section className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Before Project Started</h3>
                  <PhotoCarousel
                    photos={photos}
                    largePhotos={largePhotos}
                    projectTag="before_project"
                    onDeleteClick={handlePhotoDelete}
                    onError={handlePhotoError}
                  />
                </section>

                {/* After Project Photos */}
                <section className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-3">After Project Completed</h3>
                  <PhotoCarousel
                    photos={photos}
                    largePhotos={largePhotos}
                    projectTag="after_project"
                    onDeleteClick={handlePhotoDelete}
                    onError={handlePhotoError}
                  />
                </section>

                <div className="flex justify-center">
                  <Button size="medium" onClick={handleUploadClick}>
                    <Upload size={18} />
                    Upload More Photos
                  </Button>
                </div>
              </div>
            )}

            {!selectedProjectId && !loading && (

                                   <Alert variant='info'>
                  Please select a project from the dropdown above to view its photos.
                </Alert>

            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PhotoPage;